import { useState } from 'react';
import { sendEmailsInSequence } from '@/utils/emailService';

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function ContactSection() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    schoolName: '', board: '', studentStrength: '', currentInfrastructure: '',
    solutionsInterested: [] as string[], budgetRange: '', timeline: '',
    principalName: '', designation: '', email: '', phone: '', address: '',
    preferredDate: '', preferredTime: '', notes: ''
  });
  const [emailTouched, setEmailTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const emailError = emailTouched && formData.email.length > 0 && !isValidEmail(formData.email);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      solutionsInterested: prev.solutionsInterested.includes(value)
        ? prev.solutionsInterested.filter(item => item !== value)
        : [...prev.solutionsInterested, value]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(formData.email)) { setEmailTouched(true); return; }
    setIsSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) =>
        formBody.append(k, Array.isArray(v) ? v.join(', ') : v)
      );
      const response = await fetch('https://readdy.ai/api/form/d5jm5qpugl990ce155v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        await sendEmailsInSequence({
          subject: `New Consultation Request - ${formData.schoolName}`,
          formType: 'Schedule Consultation',
          replyTo: formData.email,
          fields: {
            'School Name': formData.schoolName, 'Board': formData.board,
            'Student Strength': formData.studentStrength, 'Current Infrastructure': formData.currentInfrastructure,
            'Solutions Interested': formData.solutionsInterested.join(', '), 'Budget Range': formData.budgetRange,
            'Implementation Timeline': formData.timeline, 'Principal Name': formData.principalName,
            'Designation': formData.designation, 'Email': formData.email, 'Phone': formData.phone,
            'Address': formData.address, 'Preferred Date': formData.preferredDate,
            'Preferred Time': formData.preferredTime, 'Notes': formData.notes || 'N/A',
          },
        });
        setSubmitStatus('success');
        setFormData({ schoolName: '', board: '', studentStrength: '', currentInfrastructure: '',
          solutionsInterested: [], budgetRange: '', timeline: '', principalName: '', designation: '',
          email: '', phone: '', address: '', preferredDate: '', preferredTime: '', notes: '' });
        setEmailTouched(false);
        setCurrentStep(1);
      } else { setSubmitStatus('error'); }
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  const nextStep = () => {
    if (currentStep === 3) { setEmailTouched(true); if (!isValidEmail(formData.email)) return; }
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const inputCls = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm";
  const selectCls = `${inputCls} cursor-pointer`;

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-teal-50 to-emerald-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Schedule Your Consultation</h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Take the first step towards transforming your school into a STEM innovation hub
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-10 border border-gray-100">
              {/* Progress */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="flex items-center flex-1">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm transition-all ${currentStep >= step ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {currentStep > step ? <i className="ri-check-line text-lg"></i> : step}
                      </div>
                      {step < 4 && <div className={`flex-1 h-1 mx-2 transition-all ${currentStep > step ? 'bg-teal-600' : 'bg-gray-200'}`}></div>}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs font-medium text-gray-500">
                  <span>School Details</span><span>Requirements</span><span>Contact Info</span><span>Timeline</span>
                </div>
              </div>

              <form id="consultation-form" data-readdy-form onSubmit={handleSubmit}>
                {currentStep === 1 && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">School Name *</label>
                      <input type="text" name="schoolName" value={formData.schoolName} onChange={handleInputChange} required className={inputCls} placeholder="Enter your school name" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Board Affiliation *</label>
                      <select name="board" value={formData.board} onChange={handleInputChange} required className={selectCls}>
                        <option value="">Select Board</option>
                        {['CBSE','ICSE','State Board','IB','Other'].map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Student Strength *</label>
                      <select name="studentStrength" value={formData.studentStrength} onChange={handleInputChange} required className={selectCls}>
                        <option value="">Select Range</option>
                        {['Less than 500','500-1000','1000-2000','2000+'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Current STEM Infrastructure *</label>
                      <div className="space-y-3">
                        {['None','Basic','Advanced'].map(opt => (
                          <label key={opt} className="flex items-center cursor-pointer">
                            <input type="radio" name="currentInfrastructure" value={opt} checked={formData.currentInfrastructure === opt} onChange={handleInputChange} required className="w-4 h-4 text-teal-600 cursor-pointer" />
                            <span className="ml-3 text-sm text-gray-700">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Solutions Interested In * (Select all that apply)</label>
                      <div className="space-y-3">
                        {['STEM Lab','AI & Robotics Lab','Innovation Workshops','Career Guidance'].map(opt => (
                          <label key={opt} className="flex items-center cursor-pointer">
                            <input type="checkbox" checked={formData.solutionsInterested.includes(opt)} onChange={() => handleCheckboxChange(opt)} className="w-4 h-4 text-teal-600 rounded cursor-pointer" />
                            <span className="ml-3 text-sm text-gray-700">{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Budget Range *</label>
                      <select name="budgetRange" value={formData.budgetRange} onChange={handleInputChange} required className={selectCls}>
                        <option value="">Select Range</option>
                        {['5-10 Lakhs','10-20 Lakhs','20-30 Lakhs','30+ Lakhs'].map(b => <option key={b} value={b}>₹{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Implementation Timeline *</label>
                      <select name="timeline" value={formData.timeline} onChange={handleInputChange} required className={selectCls}>
                        <option value="">Select Timeline</option>
                        <option value="Immediate">Immediate (Within 1 month)</option>
                        <option value="3 months">Within 3 months</option>
                        <option value="6 months">Within 6 months</option>
                        <option value="Next academic year">Next Academic Year</option>
                      </select>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Principal/Decision Maker Name *</label>
                      <input type="text" name="principalName" value={formData.principalName} onChange={handleInputChange} required className={inputCls} placeholder="Enter full name" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Designation *</label>
                      <input type="text" name="designation" value={formData.designation} onChange={handleInputChange} required className={inputCls} placeholder="e.g., Principal, Director" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="text" name="email" value={formData.email}
                        onChange={handleInputChange} onBlur={() => setEmailTouched(true)} required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm transition-colors ${emailError ? 'border-red-500 focus:ring-red-400 bg-red-50' : 'border-gray-300 focus:ring-teal-600'}`}
                        placeholder="your.email@school.com"
                      />
                      {emailError && <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600"><i className="ri-error-warning-fill"></i> Invalid email address</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className={inputCls} placeholder="+91 98765 43210" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">School Address *</label>
                      <textarea name="address" value={formData.address} onChange={handleInputChange} required rows={3} maxLength={500} className={inputCls} placeholder="Complete address with city and state"></textarea>
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Consultation Date *</label>
                      <input type="date" name="preferredDate" value={formData.preferredDate} onChange={handleInputChange} required min={new Date().toISOString().split('T')[0]} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Time Slot *</label>
                      <select name="preferredTime" value={formData.preferredTime} onChange={handleInputChange} required className={selectCls}>
                        <option value="">Select Time</option>
                        <option value="9:00 AM - 11:00 AM">9:00 AM – 11:00 AM</option>
                        <option value="11:00 AM - 1:00 PM">11:00 AM – 1:00 PM</option>
                        <option value="2:00 PM - 4:00 PM">2:00 PM – 4:00 PM</option>
                        <option value="4:00 PM - 6:00 PM">4:00 PM – 6:00 PM</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Notes</label>
                      <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={4} maxLength={500} className={inputCls} placeholder="Any specific requirements or questions..."></textarea>
                      <p className="text-xs text-gray-400 text-right mt-1">{formData.notes.length}/500</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
                  {currentStep > 1 && (
                    <button type="button" onClick={prevStep} className="px-5 py-2.5 border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer">
                      <i className="ri-arrow-left-line mr-1"></i> Back
                    </button>
                  )}
                  {currentStep < 4 ? (
                    <button type="button" onClick={nextStep} className="ml-auto px-6 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer">
                      Next <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  ) : (
                    <button type="submit" disabled={isSubmitting} className="ml-auto px-8 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                      {isSubmitting ? 'Submitting...' : 'Schedule Consultation'}
                    </button>
                  )}
                </div>

                {submitStatus === 'success' && (
                  <div className="mt-5 p-4 bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-lg text-sm">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    Thank you! We&apos;ll contact you shortly to confirm your consultation.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-5 p-4 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm">
                    <i className="ri-error-warning-fill mr-2"></i>
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-teal-700 to-emerald-700 rounded-3xl p-8 text-white sticky top-24">
              <h3 className="text-xl font-bold mb-6">What Happens Next?</h3>
              <div className="space-y-5 mb-8">
                {[
                  { icon: 'ri-phone-line', title: 'Confirmation Call', desc: "We'll call within 24 hours to confirm your consultation" },
                  { icon: 'ri-presentation-line', title: 'Detailed Presentation', desc: "Customized proposal based on your school's needs" },
                  { icon: 'ri-file-list-3-line', title: 'Implementation Plan', desc: 'Complete roadmap with timeline and investment details' }
                ].map((s, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${s.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-0.5">{s.title}</h4>
                      <p className="text-xs text-teal-100">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/20 pt-5 space-y-3">
                <h4 className="font-semibold text-sm mb-3">Or Contact Us Directly</h4>
                <a href="tel:+917826019010" className="flex items-center gap-3 text-white hover:text-teal-100 transition-all cursor-pointer text-sm">
                  <i className="ri-phone-fill text-lg"></i><span>+91 7826019010</span>
                </a>
                <a href="mailto:info@stemforgetechlabs.in" className="flex items-center gap-3 text-white hover:text-teal-100 transition-all cursor-pointer text-sm">
                  <i className="ri-mail-fill text-lg"></i><span>info@stemforgetechlabs.in</span>
                </a>
                <a href="https://wa.me/917826019010" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-teal-100 transition-all cursor-pointer text-sm">
                  <i className="ri-whatsapp-fill text-lg"></i><span>WhatsApp Us</span>
                </a>
                <div className="text-xs text-teal-100 mt-3">
                  <strong>Office Hours:</strong><br />
                  Mon–Fri: 9:00 AM – 6:00 PM<br />
                  Sat: 10:00 AM – 4:00 PM
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <a href="https://wa.me/917826019010" target="_blank" rel="noopener noreferrer" className="bg-emerald-500 text-white rounded-2xl p-6 hover:bg-emerald-600 transition-all cursor-pointer">
            <i className="ri-whatsapp-fill text-2xl mb-2 block"></i>
            <h4 className="text-base font-bold mb-1">Chat with Us</h4>
            <p className="text-emerald-100 text-xs mb-2">Quick responses on WhatsApp</p>
            <p className="font-semibold text-sm">+91 7826019010</p>
          </a>
          <a href="mailto:info@stemforgetechlabs.in" className="bg-teal-600 text-white rounded-2xl p-6 hover:bg-teal-700 transition-all cursor-pointer">
            <i className="ri-mail-fill text-2xl mb-2 block"></i>
            <h4 className="text-base font-bold mb-1">Email Us</h4>
            <p className="text-teal-100 text-xs mb-2">Send detailed inquiry</p>
            <p className="font-semibold text-sm">info@stemforgetechlabs.in</p>
          </a>
          <a href="tel:+917826019010" className="bg-orange-500 text-white rounded-2xl p-6 hover:bg-orange-600 transition-all cursor-pointer">
            <i className="ri-phone-fill text-2xl mb-2 block"></i>
            <h4 className="text-base font-bold mb-1">Call Us</h4>
            <p className="text-orange-100 text-xs mb-2">Speak to our consultant</p>
            <p className="font-semibold text-sm">+91 7826019010</p>
          </a>
        </div>
      </div>
    </section>
  );
}
